/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { KeyValuePair, SeeMoreSummary, ThemeTokens } from '@acp/core-ui';
import { ActiveNoticePropertyTag, ReportedFindingData } from '@acp/shared';
import {
    BorderProps,
    Box,
    Card,
    CardBody,
    CardBodyProps,
    GridItem,
    GridItemProps,
    Hide,
    HStack,
    Link,
    Popover,
    PopoverArrow,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    Show,
    Text,
    Tooltip,
    VStack,
} from '@chakra-ui/react';
import { parseISO, formatDistanceToNow, format } from 'date-fns';
import { Link as RouterLink } from 'react-router-dom';
import { ReportedFindingItemList } from '../wizard/reported-finding-item-list';

const gap = 2;

const gridAreaProps: { [key: string]: GridItemProps } = {
    address: {
        gridArea: 'address',
        as: VStack,
        alignItems: 'start',
        justifyContent: 'flex-start',
        gap,
    },
    service: {
        gridArea: 'service',
    },
    'service-date': {
        gridArea: 'service-date',
    },
    'reported-findings': {
        gridArea: 'reported-findings',
    },
    status: {
        gridArea: 'status',
    },
    actions: {
        gridArea: 'actions',
        as: HStack,
        justifySelf: 'flex-end',
        gap,
    },
};

type AreaType = keyof typeof gridAreaProps;

interface RowColumnProps extends GridItemProps {
    area: AreaType;
}

export function RowLayout({
    children,
    borderLeftColor = 'secondary.500',
}: {
    children: React.ReactNode;
    borderLeftColor?: BorderProps['borderLeftColor'];
}) {
    return (
        <Card
            borderColor={ThemeTokens.Defaults.borderColor}
            borderWidth={1}
            borderLeftWidth={6}
            borderLeftColor={borderLeftColor}
            bg="white"
            variant="filled"
            w="full"
            shadow="sm"
            p={5}
        >
            {children}
        </Card>
    );
}

function RowBody({ children, as: Component, ...props }: CardBodyProps) {
    return (
        <CardBody as={Component} {...props}>
            {children}
        </CardBody>
    );
}

function RowColumn({ children, area, ...props }: RowColumnProps) {
    const gridItemAreaProps = gridAreaProps[area];

    return (
        <GridItem {...gridItemAreaProps} {...props}>
            {children}
        </GridItem>
    );
}

function Address({
    propertyId,
    addressLine1,
    addressLine2,
    tags,
}: {
    propertyId: number;
    addressLine1: string;
    addressLine2: string;
    tags: string[];
}) {
    return (
        <Link
            as={RouterLink}
            to={`/property/${propertyId}`}
            textDecoration="none"
            _hover={{ textDecoration: 'underline' }}
            fontWeight="medium"
            color={!tags.includes(ActiveNoticePropertyTag.Cancelled) ? 'black' : 'muted.500'}
        >
            <VStack alignItems="start" gap={0}>
                <Text>{addressLine1}</Text>
                <Text>{addressLine2}</Text>
            </VStack>
        </Link>
    );
}

function ReportedFindingsSummary({
    reportedFindings,
}: {
    reportedFindings: ReportedFindingData[];
}) {
    return (
        <Popover trigger="hover" isLazy>
            <PopoverTrigger>
                <Box maxW="fit-content">
                    <SeeMoreSummary
                        name={reportedFindings[0].originalProblemItem.title}
                        remainingCount={reportedFindings.length - 1}
                        hasIcon={false}
                    />
                </Box>
            </PopoverTrigger>
            <PopoverContent>
                <PopoverArrow />
                <PopoverBody>
                    <VStack align="left" gap={2}>
                        <Box>
                            <Text fontSize="small" fontWeight="bold">
                                Reported Findings
                            </Text>
                            <ReportedFindingItemList reportedFindings={reportedFindings} />
                        </Box>
                    </VStack>
                </PopoverBody>
            </PopoverContent>
        </Popover>
    );
}

function ReportedFindings({ reportedFindings }: { reportedFindings: ReportedFindingData[] }) {
    return (
        <>
            <Show above={ThemeTokens.Defaults.sidebarBreakpoint}>
                <ReportedFindingsSummary reportedFindings={reportedFindings} />
                <Tooltip
                    label={`Notified on ${format(
                        parseISO(reportedFindings[0].problemRegister.originalBookingDate),
                        'dd/MM/yyyy'
                    )}`}
                    bgColor="white"
                    color="black"
                >
                    <Text mt={1} fontSize="xs" fontWeight="bold" cursor="default" color="muted.500">
                        {`Notified ${formatDistanceToNow(
                            parseISO(reportedFindings[0].problemRegister.originalBookingDate),
                            { addSuffix: true }
                        )}`}
                    </Text>
                </Tooltip>
            </Show>
            <Hide above={ThemeTokens.Defaults.sidebarBreakpoint}>
                <KeyValuePair label="Reported Findings" align="flex-start" spacing={0}>
                    <ReportedFindingsSummary reportedFindings={reportedFindings} />
                    <Tooltip
                        label={`Notified on ${format(
                            parseISO(reportedFindings[0].problemRegister.originalBookingDate),
                            'dd/MM/yyyy'
                        )}`}
                        bgColor="white"
                        color="black"
                    >
                        <Text fontSize="xs" fontWeight="bold" cursor="default" color="muted.500">
                            {`${formatDistanceToNow(
                                parseISO(reportedFindings[0].problemRegister.originalBookingDate),
                                { addSuffix: true }
                            )}`}
                        </Text>
                    </Tooltip>
                </KeyValuePair>
            </Hide>
        </>
    );
}

RowLayout.Body = RowBody;
RowLayout.Column = RowColumn;
RowLayout.Address = Address;
RowLayout.ReportedFindings = ReportedFindings;
